import { Alert, Button, Card, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { verifyEmail } from '../api/gen';

/** Where the confirmation mail lands; the server puts this path in the link. */
export const VERIFY_PATH = '/auth/verify-email';

/**
 * The token is in the query string because that is what the server put in
 * the link; the page spends it once, on load, and then the address is real
 * and the account is a way in. Reachable signed in or out: confirming a
 * second address in a tab that is already signed in is an ordinary thing.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working');
  const [error, setError] = useState('');
  // Once: strict mode mounts twice, and the token is good for one use.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setState('failed');
      setError('This link has no token.');
      return;
    }
    void (async () => {
      try {
        await unwrap(verifyEmail({ body: { token } }));
        setState('done');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setState('failed');
      }
    })();
  }, [token]);

  return (
    <Center mih="100vh" p="md">
      <Card withBorder p="xl" w={400} maw="100%">
        <Stack>
          <Title order={3}>Confirm your address</Title>
          {state === 'working' && <Loader size="sm" />}
          {state === 'done' && (
            <>
              <Text size="sm">Thank you — the address is confirmed. You can sign in now.</Text>
              <Button component="a" href="/">
                Sign in
              </Button>
            </>
          )}
          {state === 'failed' && (
            <>
              <Alert color="red" variant="light" role="alert">
                {error}
              </Alert>
              <Text size="sm" c="dimmed">
                Ask for a new email from the sign-in page.
              </Text>
              <Button component="a" href="/" variant="light">
                Back to sign-in
              </Button>
            </>
          )}
        </Stack>
      </Card>
    </Center>
  );
}
